<script lang="ts" setup>
import { onBeforeUnmount } from 'vue';
import StorageService from '../../runtime/services/StorageService';
import CUploadProgress from './UploadProgress.vue';
import { uploadBatchPercent, type UploadProgressItem } from './runtime/uploadProgress';
import CUploadPreprocessModal from './modals/UploadPreprocessModal.vue';
import {
    preprocessUploadImage,
    type UploadImagePreprocessOptions
} from './runtime/imageEditor';
import { isFilePickerImageProcessingAllowed } from '../ideo/form-file/filePickerPreview';
// import VisionService from '@/modules/ai/common/services/VisionService';

interface ErrorItem
{
    file: File;
    error: any;
}

const { $t } = useLocalization();
const { $auth } = useAuth();

const props = withDefaults(defineProps<{
    scope?: string;
    folder?: number;
    modelValue: File[];
    actionsTarget?: string;
}>(), {
    scope: 'public',
    folder: null,
    actionsTarget: ''
});

const emit = defineEmits<{
    (e: 'open'): void;
    (e: 'upload-start'): void;
    (e: 'review'): void;
    (e: 'show-results', items: FileResource[]): void;
    (e: 'update:modelValue', value: File[]): void;
    (e: 'start'): void;
    (e: 'reload'): void;
    (e: 'send', value: FileResource[]): void;
    (e: 'cancel'): void;
}>();

const files = ref(null);

const batchFiles = ref<File[]>([]);
const uploadedResults = shallowRef<FileResource[]>([]);
const progressVisible = ref(false);
const transferComplete = ref(false);
const progressJob = ref(0);
const current = ref<File>(null);
const uploading = ref(false);
const totalProgress = ref(0);
const uploadProgress = ref(0);
const success = ref<File[]>([]);
const failures = ref<ErrorItem[]>([]);
const imageMaxSize = ref(2560);
const forceConvert = ref<boolean>(true);
const forceResize = ref<boolean>(true);
const preprocessSettings = reactive<UploadImagePreprocessOptions>({
    enabled: false,
    watermarkEnabled: false,
    watermarkType: 'logo',
    watermarkText: '© Edito',
    watermarkLogoSrc: null,
    watermarkX: 85,
    watermarkY: 90,
    watermarkOpacity: 70,
    watermarkSize: 32
});
const uploadPluginRef = ref(null);
const uploadPreprocessModalRef = ref<any>(null);

const filesCount = computed(() => props.modelValue.length);
const queueSize = computed(() => props.modelValue.reduce((sum, file) => sum + file.size, 0));
const queueBytes = ref(0);
const totalBytes = computed(() => uploading.value ? Math.max(1, queueBytes.value) : props.modelValue.reduce((sum, file) => sum + file.size, 1));
const progressPercent = computed(() => uploadBatchPercent(progressItems.value));

const isImageProcessingAllowed = (file: File) =>
{
    return isImage(file) && isFilePickerImageProcessingAllowed(file);
};

const hasImagesWithoutProcessing = computed(() => props.modelValue.some((file) =>
{
    return isImage(file) && !isImageProcessingAllowed(file);
}));

const hasProcessableImages = computed(() => props.modelValue.some((file) =>
{
    return isImageProcessingAllowed(file);
}));

const preprocessPreviewFile = computed(() =>
{
    return props.modelValue.find((file) =>
    {
        return isImageProcessingAllowed(file);
    }) ?? null;
});

const preprocessSummary = computed(() =>
{
    return $t('[[[kadrowanie, transformacje, filtry, znak wodny i eksport]]]');
});

const showPreprocessSettings = () =>
{
    uploadPreprocessModalRef.value?.showModal();
};

const replaceUploadFile = (source: File, replacement: File) =>
{
    triggerInput(props.modelValue.map((file) =>
    {
        return file === source ? replacement : file;
    }));
};

watch(() => props.modelValue, () =>
{
    if (!uploading.value)
    {
        totalProgress.value = 0;
        forceConvert.value = !hasImagesWithoutProcessing.value;
    }
});

const isSending = (file: File) =>
{
    return current.value === file;
};

const isSent = (file: File) =>
{
    return success.value.filter(p => p === file).length > 0;
};

const isError = (file: File) =>
{
    return failures.value.filter(p => p.file === file).length > 0;
};

const isWaiting = (file: File) =>
{
    return !isSending(file) && !isSent(file) && !isError(file);
};

const errorMessage = (file: File) =>
{
    const failure = failures.value.find(p => p.file === file);

    if (failure && failure.error && failure.error.message)
        return failure.error.message;

    return $t('[[[Nie udało się wysłać pliku]]]');
};

const progressItems = computed<UploadProgressItem[]>(() => batchFiles.value.map((file, id) =>
{
    const status = isError(file) ? 'error' : isSent(file) ? 'success' : isSending(file) ? transferComplete.value ? 'processing' : 'sending' : 'waiting';
    const progress = status === 'success' || status === 'processing' ? 100 : status === 'sending' ? Math.round(Math.max(0, Math.min(100, uploadProgress.value / Math.max(1, file.size) * 100))) : 0;
    return { id, name: file.name, status, progress, error: status === 'error' ? errorMessage(file) : undefined };
}));
function showProgress()
{
    progressVisible.value = true;
}

const openDialog = () =>
{
    files.value.click();
    emit('open');
};

const triggerStart = () =>
{
    emit('start');
};

const triggerSend = (value: FileResource[]) =>
{
    emit('send', value);
};

const triggerCancel = () =>
{
    emit('cancel');
};

const triggerInput = (value: File[]) =>
{
    triggerStart();

    emit('update:modelValue', value);
};

const remove = (file: File) =>
{
    triggerInput(props.modelValue.filter(p => p !== file));
};

const onChange = (event: Event) =>
{
    const result: File[] = [];
    const eventFiles = (event.target as HTMLInputElement).files;
    if (!eventFiles) return;

    props.modelValue.forEach(p => result.push(p));

    for (let i = 0; i < eventFiles.length; i++)
    {
        result.push(eventFiles.item(i));
    }

    triggerInput(result);
    (event.target as HTMLInputElement).value = '';
};

let activeRequest: XMLHttpRequest | null = null;
let disposed = false;
let uploadRun = 0;
let uploadLocation: { scope: string; folder: number | null } | null = null;

function parseResponse(request: XMLHttpRequest)
{
    const message = $t('[[[Podczas przesyłania plików wystąpił nieznany błąd]]]');
    let payload: any;
    try
    {
        payload = JSON.parse(request.response);
    }
    catch
    {
        throw new Error(message);
    }
    if (request.status >= 200 && request.status < 400 && payload)
        return payload;
    throw payload || new Error(message);
}

onBeforeUnmount(() =>
{
    disposed = true;
    uploadRun++;
    uploading.value = false;
    activeRequest?.abort();
});

const sendRequest = async (file: File, run: number) =>
{
    current.value = file;
    transferComplete.value = false;
    uploadProgress.value = 0;

    const imageProcessingAllowed = isImageProcessingAllowed(file);
    const uploadFile = preprocessSettings.enabled && imageProcessingAllowed ? await preprocessUploadImage(file, preprocessSettings) : file;

    if (disposed || run !== uploadRun || !uploading.value)
        throw new DOMException('Upload cancelled', 'AbortError');

    return new Promise((resolve, reject) =>
    {
        const formData = new FormData();
        const req = new XMLHttpRequest();
        activeRequest = req;
        const destination = uploadLocation ?? { scope: props.scope, folder: props.folder };

        formData.append('file', uploadFile, uploadFile.name);
        formData.append('container', 'default');
        formData.append('scope', destination.scope);
        formData.append('forceConvert', imageProcessingAllowed && forceConvert.value ? 'true' : 'false');
        formData.append('forceResize', imageProcessingAllowed && forceResize.value ? 'true' : 'false');

        if (destination.folder)
        {
            formData.append('folder', destination.folder.toString());
        }

        req.upload.addEventListener('progress', (event) =>
        {
            if (!disposed && run === uploadRun && event.lengthComputable)
            {
                uploadProgress.value = file.size * event.loaded / Math.max(1, event.total);
            }
        });

        req.upload.addEventListener('load', () =>
        {
            if (disposed || run !== uploadRun) return;
            transferComplete.value = true;
            uploadProgress.value = 0;
            totalProgress.value += file.size;
        });

        req.upload.addEventListener('error', () =>
        {
            if (disposed || run !== uploadRun) return;
            uploadProgress.value = 0;
            totalProgress.value += file.size;
        });

        req.addEventListener('load', () =>
        {
            try
            {
                resolve(parseResponse(req));
            }
            catch (error)
            {
                reject(error);
            }
        });
        req.addEventListener('error', () => reject(new Error($t('[[[Nie udało się wysłać pliku. Sprawdź połączenie.]]]'))));
        req.addEventListener('abort', () => reject(new DOMException('Upload cancelled', 'AbortError')));
        req.addEventListener('loadend', () =>
        {
            if (activeRequest === req) activeRequest = null;
        });

        req.open('POST', StorageService.uploadUrl(), true);
        req.setRequestHeader('Authorization', 'Bearer ' + $auth.token());
        req.send(formData);
    });
};

const uploadFiles = async () =>
{
    if (uploading.value || disposed) return;
    const run = ++uploadRun;
    uploadLocation = { scope: props.scope, folder: props.folder ?? null };
    const queuedFiles = [...props.modelValue];
    if (!queuedFiles.length) return;
    batchFiles.value = queuedFiles;
    uploadedResults.value = [];
    progressJob.value++;
    progressVisible.value = true;
    queueBytes.value = queuedFiles.reduce((sum, file) => sum + file.size, 0);
    uploading.value = true;
    emit('upload-start');
    totalProgress.value = 0;
    success.value = [];
    failures.value = [];
    const uploadedResources = [] as FileResource[];

    for (let i = 0; i < queuedFiles.length; i++)
    {
        if (uploading.value && !disposed && run === uploadRun)
        {
            const file = queuedFiles[i];

            try
            {
                const resourceFile = await sendRequest(file, run);

                if (disposed || run !== uploadRun) return;
                await uploadPluginRef.value?.postProcess(resourceFile);
                if (disposed || run !== uploadRun) return;

                uploadedResources.push(resourceFile);
                success.value.push(file);
            }
            catch (ex: any)
            {
                if (disposed || run !== uploadRun) return;
                if (ex?.name === 'AbortError') break;
                failures.value.push({
                    file: file,
                    error: ex
                });
            }
        }
    }

    if (disposed || run !== uploadRun) return;

    if (uploading.value)
    {
        nextTick(() =>
        {
            if (disposed || run !== uploadRun) return;
            const newlyAdded = props.modelValue.filter(file => !queuedFiles.includes(file));
            triggerInput([...failures.value.map(p => p.file), ...newlyAdded]);

        });
    }

    if (props.modelValue.length === 0)
    {
        totalProgress.value = 0;
    }

    current.value = null;
    uploading.value = false;
    uploadLocation = null;

    uploadedResults.value = uploadedResources;
    if (uploadedResources.length) triggerSend(uploadedResources);
};

const cancel = async () =>
{
    uploadRun++;
    if (uploading.value)
    {
        uploading.value = false;
        activeRequest?.abort();
    }
    else
    {
        success.value = [];
        failures.value = [];

        progressVisible.value = false;
        batchFiles.value = [];
        triggerInput([]);
        triggerCancel();
    }
};

defineExpose({
    openDialog,
    uploading,
    progressPercent,
    showProgress
});
</script>

<template>
    <div class="min-w-0">
        <input ref="files" type="file" class="hidden" multiple @change="onChange">
        <div v-if="filesCount > 0" class="flex min-w-0 flex-col gap-[12px]">
            <div class="flex flex-wrap items-center justify-between gap-[8px]">
                <div class="flex flex-wrap items-center gap-[6px]"><span class="text-ui-helper font-[500]">{{ $t('[[[Pliki w kolejce]]]') }}: {{ filesCount }}</span><span class="text-ui-meta text-fg-faint dark:text-fg-faint-dark">· {{ $filters.filesize(queueSize) }}</span></div>
                <IdeoAction variant="secondary" icon="plus" :text="$t('[[[Dodaj kolejne]]]')" @click="openDialog" />
            </div>
            <div v-if="uploading" class="flex flex-col gap-[6px]" role="status">
                <div class="flex justify-between gap-[8px] text-ui-helper text-fg-muted dark:text-fg-muted-dark"><span>{{ $t('[[[Przesyłanie plików]]]') }}</span><span class="tabular-nums">{{ progressPercent }}%</span></div>
                <div class="h-[6px] overflow-hidden rounded-full bg-surface-3 dark:bg-surface-3-dark" role="progressbar" :aria-label="$t('[[[Postęp wysyłania]]]')" :aria-valuenow="progressPercent" :aria-valuemin="0" :aria-valuemax="100"><div class="h-full rounded-full bg-accent [transition:width_0.15s_ease] motion-reduce:transition-none" :style="{ width: `${progressPercent}%` }" /></div>
            </div>
            <Responsive table-overflow>
                <ul class="m-0 flex max-h-[320px] list-none flex-col overflow-y-auto rounded-[8px] border border-border p-0 dark:border-border-dark">
                    <li v-for="(file, index) in modelValue" :key="`${index}.${file.name}`" class="flex min-w-0 items-center gap-[10px] border-b border-border-soft px-[10px] py-[8px] last:border-b-0 dark:border-border-soft-dark" :class="isError(file) ? 'bg-danger-soft dark:bg-danger-soft-dark' : ''">
                        <span class="flex size-[30px] shrink-0 items-center justify-center rounded-[6px] bg-surface-2 dark:bg-surface-2-dark"><Loader v-if="isSending(file)" :size="16" /><IdeoIcon v-else :name="isError(file) ? 'triangle-alert' : isSent(file) ? 'check' : file.type?.startsWith('image/') ? 'image' : 'file'" :size="16" :class="isError(file) ? 'text-danger' : isSent(file) ? 'text-success' : 'text-fg-muted dark:text-fg-muted-dark'" aria-hidden="true" /></span>
                        <div class="min-w-0 flex-1">
                            <IdeoTooltip :tooltip="file.name" class="block min-w-0 truncate text-ui-helper font-[500]">{{ file.name }}</IdeoTooltip>
                            <div class="mt-[2px] flex min-w-0 flex-wrap items-center gap-x-[6px] gap-y-[2px] text-ui-meta text-fg-faint dark:text-fg-faint-dark"><span class="shrink-0 tabular-nums">{{ $filters.filesize(file.size) }}</span><span aria-hidden="true">·</span><span v-if="isError(file)" class="break-words text-danger">{{ errorMessage(file) }}</span><span v-else>{{ isSent(file) ? $t('[[[Wysłano]]]') : isSending(file) ? $t('[[[Wysyłanie…]]]') : $t('[[[Oczekuje]]]') }}</span></div>
                        </div>
                        <IdeoAction display="toolbar" icon="x" :disabled="uploading" :aria-label="$t('[[[Usuń plik z listy]]]') + ': ' + file.name" :title="$t('[[[Usuń plik z listy]]]')" @click="remove(file)" />
                    </li>
                </ul>
            </Responsive>
            <section v-if="hasProcessableImages" class="flex min-w-0 flex-col gap-[12px] border-t border-border pt-[15px] dark:border-border-dark" :aria-label="$t('[[[Przygotowanie obrazów]]]')">
                <IdeoFormGroup :label="$t('[[[Format obrazów]]]')" nospace><IdeoFormSegmented v-model="forceConvert" :options="[{ value: true, text: 'WebP' }, { value: false, text: $t('[[[Oryginalny]]]') }]" :disabled="uploading" :aria-label="$t('[[[Format obrazów]]]')" /></IdeoFormGroup>
                <IdeoFormCheckbox class="w-fit max-w-full" v-model="forceResize" :disabled="uploading">
                    {{ $t('[[[Optymalizuj rozdzielczość obrazów]]]') }}
                    <template #help>{{ $t('[[[Maksymalna rozdzielczość: %0 × %1 px.|||{0}|||{1}]]]', imageMaxSize, imageMaxSize) }} {{ $t('[[[Nie dotyczy plików SVG i ICO.]]]') }}</template>
                </IdeoFormCheckbox>
                <IdeoFormCheckbox class="w-fit max-w-full" v-model="preprocessSettings.enabled" :disabled="uploading">{{ $t('[[[Modyfikuj obrazy przed wysłaniem]]]') }}</IdeoFormCheckbox>
                <div v-if="preprocessSettings.enabled" class="flex min-w-0 flex-col gap-[8px]">
                    <IdeoAction class="self-start" variant="secondary" icon="pencil" :disabled="uploading" :text="$t('[[[Zmodyfikuj obraz]]]')" @click="showPreprocessSettings" />
                    <p class="m-0 text-ui-helper text-fg-muted dark:text-fg-muted-dark">{{ preprocessSummary }}</p>
                </div>
                <FilemanagerUpload ref="uploadPluginRef" />
            </section>
            <Portal :to="props.actionsTarget || 'file-upload-actions'" :disabled="!props.actionsTarget">
            <div class="flex w-full flex-wrap items-center justify-end gap-[8px]" :class="props.actionsTarget ? '' : 'border-t border-border py-[12px] dark:border-border-dark'">
                <IdeoAction variant="secondary" icon="x" :disabled="uploading" :text="$t('[[[Wyczyść kolejkę]]]')" @click="cancel()" />
                <IdeoAction variant="primary" icon="cloud-upload" :disabled="uploading" :loading="uploading" :text="$t('[[[Wyślij pliki]]]')" @click="uploadFiles()" />
            </div>
            </Portal>
        </div>
        <CUploadProgress :visible="progressVisible" :running="uploading" :job="progressJob" :items="progressItems" :percent="progressPercent" :can-show-result="uploadedResults.length > 0" @show-result="emit('show-results', uploadedResults)" @close="progressVisible = false" @review="emit('review')" />
        <CUploadPreprocessModal ref="uploadPreprocessModalRef" v-model="preprocessSettings" :preview-file="preprocessPreviewFile" @cropped="replaceUploadFile" />
    </div>
</template>
